// src/utils/share.ts
// External share helper (native share sheet + clipboard fallback)

type SharePayload = {
  title?: string;
  text?: string;
  url?: string;
};

const PROD_ORIGIN = 'https://www.roddo.app';

const cleanId = (value: unknown) => String(value ?? '').trim();

/** Always share production links (even from localhost). */
export function getShareOrigin() {
  return PROD_ORIGIN;
}

function buildRoddoAppUrl(params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, rawValue]) => {
    const value = cleanId(rawValue);
    if (value) search.set(key, value);
  });
  const query = search.toString();
  return `${getShareOrigin()}/app${query ? `?${query}` : ''}`;
}

export function buildPublicPostUrl(postId: string) {
  const safeId = cleanId(postId);
  return `${getShareOrigin()}/app/p/${encodeURIComponent(safeId)}`;
}

export function buildTournamentShareUrl(tournamentId: string) {
  const safeId = cleanId(tournamentId);
  return `${getShareOrigin()}/app/t/${encodeURIComponent(safeId)}`;
}

export function buildBazaarListingShareUrl(itemId: string) {
  const safeId = cleanId(itemId);
  return `${getShareOrigin()}/app/b/${encodeURIComponent(safeId)}`;
}

/**
 * Group-crew share links are navigation links for existing crew members.
 * They do not grant membership by themselves; private crew membership still
 * stays server/Firestore controlled.
 */
export function buildCrewShareUrl(crewId: string) {
  const safeId = cleanId(crewId);
  return `${getShareOrigin()}/app/crew/${encodeURIComponent(safeId)}`;
}

export function buildCrewPostShareUrl(crewId: string, postId: string) {
  const safeCrewId = cleanId(crewId);
  const safePostId = cleanId(postId);
  return `${getShareOrigin()}/app/crew/${encodeURIComponent(safeCrewId)}/post/${encodeURIComponent(safePostId)}`;
}

/**
 * A personal Crew invite points at the inviter's public Roddo identity. Opening
 * the URL never creates a relationship automatically; the recipient still has
 * to explicitly send/accept the Crew request inside Roddo.
 */
export function buildCrewInviteShareUrl(userId: string) {
  const safeId = cleanId(userId);
  return `${getShareOrigin()}/app/invite/crew/${encodeURIComponent(safeId)}`;
}

async function copyWithFallback(text: string) {
  const value = String(text || '');

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {}

  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', 'true');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export async function share(payload: SharePayload) {
  // Roddo external shares are intentionally URL-only. Keep title/text on the
  // payload type for compatibility with older callers, but never send them to
  // the native share sheet or clipboard fallback.
  const url = String(payload.url || '').trim();
  if (!url) return { ok: false, method: 'clipboard' as const };

  try {
    const nav: any = navigator as any;
    if (nav?.share) {
      await nav.share({ url });
      return { ok: true, method: 'native' as const };
    }
  } catch {}

  const copied = await copyWithFallback(url);
  return { ok: copied, method: 'clipboard' as const };
}

export function buildSavedSpotShareUrl(spot: { lat: number; lng: number; name?: string }) {
  const lat = Number(spot?.lat ?? 0);
  const lng = Number(spot?.lng ?? 0);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

export function buildPublicProfileUrl(userId: string) {
  const safeId = cleanId(userId);
  return `${getShareOrigin()}/app/u/${encodeURIComponent(safeId)}`;
}

export function buildFishIQShareUrl(speciesId: string) {
  const safeId = cleanId(speciesId);
  return `${getShareOrigin()}/app/fishiq/${encodeURIComponent(safeId)}`;
}

export function buildAchievementShareUrl(achievementId: string) {
  const safeId = cleanId(achievementId);
  return `${getShareOrigin()}/app/a/${encodeURIComponent(safeId)}`;
}

export function buildCatchShareUrl(catchId: string) {
  const safeId = cleanId(catchId);
  return `${getShareOrigin()}/app/c/${encodeURIComponent(safeId)}`;
}

export function buildGenericSharePayload(input: {
  type: 'catch' | 'fishiq' | 'profile' | 'post' | 'achievement' | 'spot';
  id: string;
  title?: string;
  text?: string;
  profile?: any;
}) {
  const id = cleanId(input?.id);

  if (input.type === 'profile') return { url: buildPublicProfileUrl(id) };
  if (input.type === 'fishiq') return { url: buildFishIQShareUrl(id) };
  if (input.type === 'achievement') return { url: buildAchievementShareUrl(id) };
  if (input.type === 'catch') return { url: buildCatchShareUrl(id) };
  if (input.type === 'post') return { url: buildPublicPostUrl(id) };

  return {
    url: id
      ? `${getShareOrigin()}/app/share/${encodeURIComponent(id)}`
      : getShareOrigin(),
  };
}

// ---------------------------------------------------------------------------
// Legacy share-text compatibility exports
// ---------------------------------------------------------------------------
// External Roddo sharing is URL-only now. These helpers remain exported so
// older/current UI callers can continue importing them without breaking Vite
// during the transition. share() intentionally ignores title/text.
export function buildTournamentShareText(_tournament: any) {
  return '';
}

export function buildCrewShareText(_crew: any) {
  return '';
}

export function buildCrewPostShareText(_post: any, _crewTitle?: string) {
  return '';
}

export function buildPostShareText(_post: any) {
  return '';
}

export function buildSavedSpotShareText(_spot: { name?: string; kind?: string; note?: string }) {
  return '';
}

export function buildProfileShareText(_profile: any) {
  return '';
}
